import {
  Component,
  ElementRef,
  inject,
  signal,
  computed,
  ViewChild,
  OnInit,
  HostListener,
  OnDestroy,
  Input,
  AfterViewInit,
  Renderer2,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSplitterModule } from 'ng-zorro-antd/splitter';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';

import { v4 as uuidv4 } from 'uuid';

import { AuthStore } from '@rapider/authentication';
import {
  DefinitionControllerService,
  Comment,
  CommentControllerService,
} from '@rapider/sdk';
import { NotificationService } from '@rapider/services';
import { DynamicPageRendererComponent } from '../dynamic-page-renderer/dynamic-page-renderer.component';
import { PageConfig } from '../dynamic-page-renderer/dynamic-page-renderer.config';
import { RapiderMarkdownEditorComponent } from '@rapider/markdown-editor';
import { MarkdownPreviewComponent } from '@rapider/markdown-preview';

@Component({
  selector: 'rapider-public-bug-report-viewer',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    NzButtonModule,
    NzTooltipModule,
    NzInputModule,
    NzSplitterModule,
    NzTagModule,
    NzPaginationModule,
    RapiderMarkdownEditorComponent,
    MarkdownPreviewComponent,
  ],
  host: {
    class:
      'flex flex-col w-full h-full min-w-0 min-h-0 overflow-hidden relative',
  },
  templateUrl: './public-bug-report-viewer.component.html',
  styles: [
    `
      ::ng-deep .comment-hover-target {
        outline: 2px dashed var(--color-danger) !important;
        outline-offset: -2px;
        cursor: crosshair !important;
      }
    `,
  ],
})
export class PublicBugReportViewerComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() inputAppId?: string;
  @Input() inputPageId?: string;
  @Input() inputDefinitionName?: string;
  @Input() targetFileName: string = 'pageTemplate';
  @Input() htmlUrl?: string;

  @Input() pageConfig!: PageConfig;
  @Input() manifestPages: any[] = [];
  @Input() initialPayload: Record<string, any> = {};

  private readonly httpClient = inject(HttpClient);
  private readonly msg = inject(NzMessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly definitionController = inject(DefinitionControllerService);
  private readonly commentController = inject(CommentControllerService);
  private readonly notificationService = inject(NotificationService);
  private readonly authStore = inject(AuthStore);
  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  @ViewChild('canvasWrapper', { static: false })
  canvasWrapper!: ElementRef<HTMLElement>;
  @ViewChild('captureZone', { static: false })
  captureZone!: ElementRef<HTMLElement>;

  private clickListenerFn?: () => void;
  private mouseMoveListenerFn?: () => void;
  private mouseOutListenerFn?: () => void;

  public isLoading = signal<boolean>(true);
  public pageId = signal<string | null>(null);
  public appId = signal<string | null>(null);
  public definitionName = signal<string | null>(null);
  public baseSha = signal<string | null>(null);
  public templateContent = signal<string>('');

  public viewMode = signal<'desktop' | 'tablet' | 'mobile'>('desktop');

  public isAddCommentMode = signal<boolean>(false);
  public showComments = signal<boolean>(true);
  public comments = signal<Comment[]>([]);
  public selectedCommentId = signal<string | null>(null);
  public isScrolling = signal<boolean>(false);

  public draftComment = signal<{
    text: string;
    offsetX: number;
    offsetY: number;
    elementId?: string;
    uniqueSelector?: string;
    absoluteX?: number;
    absoluteY?: number;
    context?: string;
    viewMode?: 'desktop' | 'tablet' | 'mobile';
  } | null>(null);

  public bubblePositions = signal<
    Record<string, { top: number; left: number; isOrphaned: boolean }>
  >({});

  // Pagination State
  public currentPage = signal<number>(1);
  public pageSize = signal<number>(10);

  public paginatedComments = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.visibleComments().slice(start, start + this.pageSize());
  });

  public visibleComments = computed(() => {
    return this.comments().sort(
      (a, b) =>
        new Date(b.createdDate || '').getTime() -
        new Date(a.createdDate || '').getTime(),
    );
  });

  public reviewFileName = computed(() => {
    return `${this.targetFileName}_initial_designReview.html`;
  });

  ngOnInit() {
    const aId = this.inputAppId || this.route.snapshot.paramMap.get('appId');
    const pId = this.inputPageId || this.route.snapshot.paramMap.get('pageId');

    if (aId) this.appId.set(aId);
    if (this.inputDefinitionName)
      this.definitionName.set(this.inputDefinitionName);

    if (pId) {
      this.pageId.set(pId);
      this.fetchBuilderData(pId);
    } else {
      this.isLoading.set(false);
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.recalculateBubblePositions();
  }

  private async fetchBuilderData(id: string): Promise<void> {
    try {
      this.isLoading.set(true);
      if (!this.appId()) {
        this.comments.set([]);
        this.isLoading.set(false);
        return;
      }

      this.commentController
        .find({
          filter: JSON.stringify({
            where: {
              relatedEntityId: this.appId(),
              relatedEntityName: 'App',
            },
          }),
        })
        .subscribe({
          next: (comments: Comment[]) => {
            // Further filter by page id in metadata if needed, or just by relatedEntityId
            // We will parse metadata to get pageId and position
            const pageComments = comments.filter((c) => {
              const meta = this.parseCommentMetadata(c);
              return meta.pageId === id;
            });
            this.comments.set(pageComments);
            this.isLoading.set(false);
            setTimeout(() => this.recalculateBubblePositions(), 500);
          },
          error: (err) => {
            console.error(
              '[PublicBugReportViewer] Failed to fetch comments:',
              err,
            );
            this.comments.set([]);
            this.isLoading.set(false);
          },
        });
    } catch (error) {
      console.error('[PublicBugReportViewer] Initialization error:', error);
      this.notificationService.createNotification(
        'error',
        'Error',
        'Failed to initialize comments.',
      );
      this.isLoading.set(false);
    }
  }

  private iframePollInterval?: any;
  private boundIframeDoc?: Document;

  ngAfterViewInit() {
    if (this.captureZone?.nativeElement) {
      // We must explicitly use addEventListener to guarantee the capture phase in standard DOM
      this.captureZone.nativeElement.addEventListener(
        'click',
        this.boundHandleClick,
        true,
      );
      this.captureZone.nativeElement.addEventListener(
        'mousedown',
        this.boundHandlePointerEvent,
        true,
      );
      this.captureZone.nativeElement.addEventListener(
        'pointerdown',
        this.boundHandlePointerEvent,
        true,
      );
      this.captureZone.nativeElement.addEventListener(
        'mouseup',
        this.boundHandlePointerEvent,
        true,
      );
      this.captureZone.nativeElement.addEventListener(
        'pointerup',
        this.boundHandlePointerEvent,
        true,
      );
      this.captureZone.nativeElement.addEventListener(
        'mousemove',
        this.boundHandleMouseMove,
        true,
      );
      this.captureZone.nativeElement.addEventListener(
        'mouseout',
        this.boundHandleMouseOut,
        true,
      );
    }

    // Poll to find an iframe if one gets injected
    this.iframePollInterval = setInterval(() => {
      const iframe = this.captureZone?.nativeElement.querySelector(
        'iframe',
      ) as HTMLIFrameElement;
      if (
        iframe &&
        iframe.contentDocument &&
        iframe.contentDocument !== this.boundIframeDoc
      ) {
        if (this.boundIframeDoc) {
          this.boundIframeDoc.removeEventListener(
            'click',
            this.boundHandleClick,
            true,
          );
          this.boundIframeDoc.removeEventListener(
            'mousedown',
            this.boundHandlePointerEvent,
            true,
          );
          this.boundIframeDoc.removeEventListener(
            'pointerdown',
            this.boundHandlePointerEvent,
            true,
          );
          this.boundIframeDoc.removeEventListener(
            'mouseup',
            this.boundHandlePointerEvent,
            true,
          );
          this.boundIframeDoc.removeEventListener(
            'pointerup',
            this.boundHandlePointerEvent,
            true,
          );
          this.boundIframeDoc.removeEventListener(
            'mousemove',
            this.boundHandleMouseMove,
            true,
          );
          this.boundIframeDoc.removeEventListener(
            'mouseout',
            this.boundHandleMouseOut,
            true,
          );
          this.boundIframeDoc.removeEventListener(
            'scroll',
            this.boundHandleScroll,
            true,
          );
          if (this.boundIframeDoc.defaultView) {
            this.boundIframeDoc.defaultView.removeEventListener(
              'click',
              this.boundHandleClick,
              true,
            );
            this.boundIframeDoc.defaultView.removeEventListener(
              'mousedown',
              this.boundHandlePointerEvent,
              true,
            );
            this.boundIframeDoc.defaultView.removeEventListener(
              'pointerdown',
              this.boundHandlePointerEvent,
              true,
            );
            this.boundIframeDoc.defaultView.removeEventListener(
              'mouseup',
              this.boundHandlePointerEvent,
              true,
            );
            this.boundIframeDoc.defaultView.removeEventListener(
              'pointerup',
              this.boundHandlePointerEvent,
              true,
            );
            this.boundIframeDoc.defaultView.removeEventListener(
              'scroll',
              this.boundHandleScroll,
              true,
            );
          }
        }
        this.boundIframeDoc = iframe.contentDocument;
        this.boundIframeDoc.addEventListener(
          'click',
          this.boundHandleClick,
          true,
        );
        this.boundIframeDoc.addEventListener(
          'mousedown',
          this.boundHandlePointerEvent,
          true,
        );
        this.boundIframeDoc.addEventListener(
          'pointerdown',
          this.boundHandlePointerEvent,
          true,
        );
        this.boundIframeDoc.addEventListener(
          'mouseup',
          this.boundHandlePointerEvent,
          true,
        );
        this.boundIframeDoc.addEventListener(
          'pointerup',
          this.boundHandlePointerEvent,
          true,
        );
        this.boundIframeDoc.addEventListener(
          'mousemove',
          this.boundHandleMouseMove,
          true,
        );
        this.boundIframeDoc.addEventListener(
          'mouseout',
          this.boundHandleMouseOut,
          true,
        );
        this.boundIframeDoc.addEventListener(
          'scroll',
          this.boundHandleScroll,
          true,
        );
        if (this.boundIframeDoc.defaultView) {
          this.boundIframeDoc.defaultView.addEventListener(
            'click',
            this.boundHandleClick,
            true,
          );
          this.boundIframeDoc.defaultView.addEventListener(
            'mousedown',
            this.boundHandlePointerEvent,
            true,
          );
          this.boundIframeDoc.defaultView.addEventListener(
            'pointerdown',
            this.boundHandlePointerEvent,
            true,
          );
          this.boundIframeDoc.defaultView.addEventListener(
            'mouseup',
            this.boundHandlePointerEvent,
            true,
          );
          this.boundIframeDoc.defaultView.addEventListener(
            'pointerup',
            this.boundHandlePointerEvent,
            true,
          );
          this.boundIframeDoc.defaultView.addEventListener(
            'scroll',
            this.boundHandleScroll,
            true,
          );
        }
      }
    }, 1000);
  }

  private boundHandlePointerEvent = (e: Event) => {
    if (this.isAddCommentMode()) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
  };

  private boundHandleClick = (e: MouseEvent) => {
    if (this.isAddCommentMode()) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.handleCaptureZoneClick(e);
    }
  };

  private scrollTimeout: any;

  private boundHandleScroll = (e: Event) => {
    if (!this.isScrolling()) {
      this.isScrolling.set(true);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = setTimeout(() => {
      this.isScrolling.set(false);
      this.recalculateBubblePositions();
    }, 150);
  };

  private boundHandleMouseMove = (e: MouseEvent) => {
    if (this.isAddCommentMode()) {
      this.handleCaptureZoneMouseMove(e);
    }
  };

  private boundHandleMouseOut = (e: MouseEvent) => {
    if (this.isAddCommentMode()) {
      this.handleCaptureZoneMouseOut(e);
    }
  };

  public setViewMode(mode: 'desktop' | 'tablet' | 'mobile') {
    this.viewMode.set(mode);
    setTimeout(() => {
      this.recalculateBubblePositions();
    }, 350);
  }

  public getCanvasWidth(): number {
    switch (this.viewMode()) {
      case 'desktop':
        return 1200;
      case 'tablet':
        return 800;
      case 'mobile':
        return 400;
      default:
        return 1200;
    }
  }

  public toggleAddCommentMode() {
    this.isAddCommentMode.set(!this.isAddCommentMode());
    if (this.isAddCommentMode()) {
      this.draftComment.set(null);
    }
  }

  public toggleCommentsVisibility() {
    this.showComments.set(!this.showComments());
  }

  public selectComment(id: string) {
    this.selectedCommentId.set(this.selectedCommentId() === id ? null : id);
    if (this.selectedCommentId()) {
      const comment = this.comments().find((c) => c.id === id);
      let switched = false;
      if (comment) {
        const meta = this.parseCommentMetadata(comment);
        const viewMode = meta.viewMode || 'desktop';
        if (this.viewMode() !== viewMode) {
          this.viewMode.set(viewMode as any);
          switched = true;
        }
      }

      if (switched) {
        setTimeout(() => {
          this.recalculateBubblePositions();
          this.scrollToBubble(id);
        }, 350);
      } else {
        this.scrollToBubble(id);
      }
    }
  }

  private scrollToBubble(commentId: string) {
    const pos = this.bubblePositions()[commentId];
    if (pos && !pos.isOrphaned) {
      const doc = this.getLocalDoc();
      if (doc) {
        doc.defaultView?.scrollTo({
          top: Math.max(0, pos.top - 100),
          behavior: 'smooth',
        });
      }
    }
  }

  private getLocalDoc(): Document {
    const iframe = this.captureZone?.nativeElement.querySelector(
      'iframe',
    ) as HTMLIFrameElement;
    if (iframe && iframe.contentDocument) {
      return iframe.contentDocument;
    }
    return this.el.nativeElement.ownerDocument;
  }

  public handleCaptureZoneMouseMove(event: MouseEvent): void {
    if (!this.isAddCommentMode()) return;
    const doc = this.getLocalDoc();
    let target = event.target as HTMLElement;
    if (
      target === doc.body ||
      target === doc.documentElement ||
      target === this.canvasWrapper.nativeElement
    )
      return;

    doc
      .querySelectorAll('.comment-hover-target')
      .forEach((el) => el.classList.remove('comment-hover-target'));
    target.classList.add('comment-hover-target');
  }

  public handleCaptureZoneMouseOut(event: MouseEvent): void {
    const doc = this.getLocalDoc();
    doc
      .querySelectorAll('.comment-hover-target')
      .forEach((el) => el.classList.remove('comment-hover-target'));
  }

  private generateUniqueSelector(el: HTMLElement): string {
    if (!el) return '';
    const path = [];
    let current: HTMLElement | null = el;
    while (current && current.nodeType === 1) {
      // Node.ELEMENT_NODE
      let selector = current.nodeName.toLowerCase();
      if (current.id) {
        selector += `#${current.id}`;
        path.unshift(selector);
        break;
      }

      let sibling = current.previousElementSibling;
      let nth = 1;
      while (sibling) {
        if (sibling.nodeName.toLowerCase() === selector) {
          nth++;
        }
        sibling = sibling.previousElementSibling;
      }
      selector += `:nth-of-type(${nth})`;
      path.unshift(selector);
      current = current.parentElement;
    }
    return path.join(' > ');
  }

  public handleCaptureZoneClick(event: MouseEvent): void {
    if (this.isAddCommentMode()) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const target = event.target as HTMLElement;
      let elementId = target.getAttribute('data-rapider-node-id');
      if (!elementId) {
        elementId = uuidv4();
        target.setAttribute('data-rapider-node-id', elementId);
      }

      const rect = target.getBoundingClientRect();

      const payload = {
        elementId: elementId,
        clientX: event.clientX,
        clientY: event.clientY,
        rect: {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        },
        uniqueSelector: this.generateUniqueSelector(target),
      };

      this.handleSandboxClick(payload);
    }
  }

  private handleSandboxClick(eventData: any): void {
    if (this.isAddCommentMode()) {
      const offsetX = eventData.clientX - eventData.rect.left;
      const offsetY = eventData.clientY - eventData.rect.top;

      this.isAddCommentMode.set(false);
      this.draftComment.set({
        text: '',
        offsetX,
        offsetY,
        elementId: eventData.elementId,
        uniqueSelector: eventData.uniqueSelector,
        absoluteX: eventData.clientX,
        absoluteY: eventData.clientY,
        context: eventData.context,
        viewMode: this.viewMode(),
      });

      this.recalculateBubblePositions();
      return;
    }
  }

  public cancelDraftComment() {
    this.draftComment.set(null);
    this.recalculateBubblePositions();
  }

  public async saveDraftComment() {
    const draft = this.draftComment();
    if (!draft || !draft.text.trim()) return;

    const user = this.authStore.user();

    const meta = {
      pageId: this.pageId(),
      appId: this.appId(),
      offsetX: draft.offsetX,
      offsetY: draft.offsetY,
      viewMode: draft.viewMode || 'desktop',
      elementId: draft.elementId || '',
      uniqueSelector: draft.uniqueSelector || '',
      absoluteX: draft.absoluteX || draft.offsetX,
      absoluteY: draft.absoluteY || draft.offsetY,
    };

    const newComment: Omit<Comment, 'id'> = {
      content: draft.text,
      category: 'Bug Report',
      status: 'Open',
      projectId: user?.['lastProjectId'] || '',
      relatedEntityName: 'App',
      relatedEntityId: this.appId()!,
      metadata: [
        { key: 'pageId', value: meta.pageId },
        { key: 'appId', value: meta.appId },
        { key: 'offsetX', value: meta.offsetX.toString() },
        { key: 'offsetY', value: meta.offsetY.toString() },
        { key: 'viewMode', value: meta.viewMode },
        { key: 'elementId', value: meta.elementId },
        { key: 'uniqueSelector', value: meta.uniqueSelector },
        { key: 'absoluteX', value: meta.absoluteX.toString() },
        { key: 'absoluteY', value: meta.absoluteY.toString() },
      ],
    };

    this.commentController.create({ body: newComment as any }).subscribe({
      next: (created) => {
        this.comments.update((c) => [...c, created]);
        this.draftComment.set(null);
        this.isAddCommentMode.set(false);
        this.notificationService.createNotification(
          'success',
          'Success',
          'Bug reported successfully',
        );
        setTimeout(() => this.recalculateBubblePositions(), 50);
      },
      error: () => {
        this.notificationService.createNotification(
          'error',
          'Error',
          'Failed to report bug',
        );
      },
    });
  }

  public recalculateBubblePositions() {
    const doc = this.getLocalDoc();
    if (!doc || !this.canvasWrapper) return;

    const canvasRect = this.canvasWrapper.nativeElement.getBoundingClientRect();

    let iframeTopOffset = 0;
    let iframeLeftOffset = 0;
    const iframe = this.captureZone?.nativeElement.querySelector(
      'iframe',
    ) as HTMLIFrameElement;
    if (iframe && doc === iframe.contentDocument) {
      const iframeRect = iframe.getBoundingClientRect();
      iframeTopOffset = iframeRect.top;
      iframeLeftOffset = iframeRect.left;
    }

    const newPositions: Record<
      string,
      { top: number; left: number; isOrphaned: boolean }
    > = {};

    this.comments().forEach((comment) => {
      const meta = this.parseCommentMetadata(comment);
      const elementId = meta.elementId;

      if (!elementId) return;

      let el = doc.querySelector(
        `[data-rapider-node-id="${elementId}"]`,
      ) as HTMLElement;

      // Fallback to DOM tree CSS path if node ID is lost (e.g. after refresh)
      if (!el && meta.uniqueSelector) {
        try {
          el = doc.querySelector(meta.uniqueSelector) as HTMLElement;
          if (el) {
            el.setAttribute('data-rapider-node-id', elementId);
          }
        } catch (e) {
          // invalid selector
        }
      }

      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) {
          return;
        }

        if (this.showComments()) {
          el.setAttribute('data-rapider-comment-id', comment.id);
        }

        const offsetY = parseFloat(meta.offsetY || '0');
        const offsetX = parseFloat(meta.offsetX || '0');

        // Check if out of bounds of iframe viewport
        if (
          iframe &&
          (rect.top + offsetY < 0 ||
            rect.top + offsetY > iframe.clientHeight ||
            rect.left + offsetX < 0 ||
            rect.left + offsetX > iframe.clientWidth)
        ) {
          return; // Skip adding to newPositions, so it won't render
        }

        newPositions[comment.id] = {
          top: rect.top + iframeTopOffset + offsetY - canvasRect.top,
          left: rect.left + iframeLeftOffset + offsetX - canvasRect.left,
          isOrphaned: false,
        };
      } else {
        const absX = parseFloat(meta.absoluteX || '0');
        const absY = parseFloat(meta.absoluteY || '0');
        const viewMode = meta.viewMode || 'desktop';

        if (
          absX !== undefined &&
          absY !== undefined &&
          viewMode === this.viewMode()
        ) {
          newPositions[comment.id] = {
            top: absY + iframeTopOffset - canvasRect.top,
            left: absX + iframeLeftOffset - canvasRect.left,
            isOrphaned: false,
          };
        }
      }
    });

    const draft = this.draftComment();
    if (draft && draft.elementId) {
      let el = doc.querySelector(
        `[data-rapider-node-id="${draft.elementId}"]`,
      ) as HTMLElement;

      if (!el && draft.uniqueSelector) {
        try {
          el = doc.querySelector(draft.uniqueSelector) as HTMLElement;
          if (el) {
            el.setAttribute('data-rapider-node-id', draft.elementId);
          }
        } catch (e) {}
      }

      if (el) {
        const rect = el.getBoundingClientRect();
        const offsetY = draft.offsetY;
        const offsetX = draft.offsetX;

        if (
          iframe &&
          (rect.top + offsetY < 0 ||
            rect.top + offsetY > iframe.clientHeight ||
            rect.left + offsetX < 0 ||
            rect.left + offsetX > iframe.clientWidth)
        ) {
          // Do not show draft bubble if out of bounds
        } else {
          newPositions['draft'] = {
            top: rect.top + iframeTopOffset + offsetY - canvasRect.top,
            left: rect.left + iframeLeftOffset + offsetX - canvasRect.left,
            isOrphaned: false,
          };
        }
      } else if (
        draft.absoluteX !== undefined &&
        draft.absoluteY !== undefined &&
        draft.viewMode === this.viewMode()
      ) {
        newPositions['draft'] = {
          top: draft.absoluteY + iframeTopOffset - canvasRect.top,
          left: draft.absoluteX + iframeLeftOffset - canvasRect.left,
          isOrphaned: false,
        };
      }
    }
    this.bubblePositions.set(newPositions);
  }

  public getSidebarBubbleClass(
    id: string,
    isOrphaned?: boolean,
    status?: string | null,
  ): string {
    let base =
      'w-6 h-6 rounded-full rounded-bl-sm flex items-center justify-center text-[11px] font-bold shadow-md shrink-0 transition-all border border-white/20';
    if (isOrphaned) {
      base += ' border-dashed border-2 bg-danger text-white';
    } else if (this.selectedCommentId() === id) {
      base += ' bg-danger text-white scale-110 ring-2 ring-danger/30';
    } else {
      base += ' bg-danger text-white opacity-90';
    }
    return base;
  }

  public getCommentCardClass(id: string, status?: string | null): string {
    const base =
      'p-3 bg-component-inner border rounded-xl shadow-sm cursor-pointer transition-all flex flex-col relative overflow-hidden';
    if (this.selectedCommentId() === id)
      return `${base} border-danger shadow-md ring-1 ring-danger/20`;
    return `${base} border-secondary-border hover:border-danger/50 hover:shadow-default`;
  }

  public getCommentHeaderClass(id: string, status?: string | null): string {
    if (this.selectedCommentId() === id) return 'border-danger/30';
    return 'border-secondary-border';
  }

  public formatBubbleNumber(num?: number | null): string {
    if (num === undefined || num === null || isNaN(num)) return '';
    const n = Math.max(0, Math.floor(num));
    if (n <= 999) {
      return String(n);
    }
    if (n <= 3599) {
      const offset = n - 1000;
      const letter = String.fromCharCode(65 + Math.floor(offset / 100));
      const digits = String(offset % 100).padStart(2, '0');
      return `${letter}${digits}`;
    }
    if (n <= 10359) {
      const offset = n - 3600;
      const pairIndex = Math.floor(offset / 10);
      const firstLetter = String.fromCharCode(65 + Math.floor(pairIndex / 26));
      const secondLetter = String.fromCharCode(65 + (pairIndex % 26));
      const digit = offset % 10;
      return `${firstLetter}${secondLetter}${digit}`;
    }
    return n.toString(36).toUpperCase();
  }

  public getCanvasBubbleClass(
    id: string,
    isOrphaned?: boolean,
    status?: string | null,
  ): string {
    let base =
      'absolute w-8 h-8 -mt-8 rounded-full rounded-bl-sm flex items-center justify-center text-xs font-bold text-white shadow-xl cursor-pointer transition-all z-[1001] origin-bottom-left border-2 border-white/30 backdrop-blur-sm';

    if (isOrphaned) {
      base += ' bg-danger border-dashed shadow-danger/40 animate-pulse';
    } else if (this.selectedCommentId() === id) {
      base = base.replace(
        'border-2 border-white/30',
        'border-[3px] border-white shadow-[0_0_15px_rgba(0,0,0,0.3)]',
      );
      base += ' bg-danger scale-125 z-[1005] animate-shake-pause';
    } else {
      base += ' bg-danger opacity-90 hover:opacity-100 hover:scale-110';
    }

    if (this.isScrolling()) {
      base += ' !opacity-0 pointer-events-none';
    }

    return base;
  }

  private parseCommentMetadata(comment: Comment): any {
    const meta: any = {};
    if (comment.metadata) {
      for (const m of comment.metadata) {
        if (m.key) {
          meta[m.key] = m.value;
        }
      }
    }
    return meta;
  }

  public getCommentViewMode(comment: Comment): string {
    const meta = this.parseCommentMetadata(comment);
    if (meta.viewMode === 'mobile') return 'Mobile';
    if (meta.viewMode === 'tablet') return 'Tablet';
    return 'Desktop';
  }

  public canDeleteComment(comment: Comment): boolean {
    const user = this.authStore.user();
    if (!user) return false;
    const isOwner =
      (comment as any)['createdByPersonId'] === (user as any)['personId'];
    const isAdmin = (user as any)['scope']?.includes('Admin');
    return isOwner || isAdmin;
  }

  public deleteComment(id: string): void {
    this.commentController.deleteById({ id }).subscribe({
      next: () => {
        this.notificationService.createNotification(
          'success',
          'Success',
          'Bug report deleted successfully',
        );
        if (this.pageId()) {
          this.fetchBuilderData(this.pageId()!);
        }
      },
      error: (err) => {
        console.error('Failed to delete bug report:', err);
        this.notificationService.createNotification(
          'error',
          'Error',
          'Failed to delete bug report',
        );
      },
    });
  }

  ngOnDestroy() {
    if (this.iframePollInterval) {
      clearInterval(this.iframePollInterval);
    }
    if (this.boundIframeDoc) {
      this.boundIframeDoc.removeEventListener(
        'click',
        this.boundHandleClick,
        true,
      );
      this.boundIframeDoc.removeEventListener(
        'mousemove',
        this.boundHandleMouseMove,
        true,
      );
      this.boundIframeDoc.removeEventListener(
        'mouseout',
        this.boundHandleMouseOut,
        true,
      );
    }
    if (this.captureZone?.nativeElement) {
      this.captureZone.nativeElement.removeEventListener(
        'click',
        this.boundHandleClick,
        true,
      );
      this.captureZone.nativeElement.removeEventListener(
        'mousemove',
        this.boundHandleMouseMove,
        true,
      );
      this.captureZone.nativeElement.removeEventListener(
        'mouseout',
        this.boundHandleMouseOut,
        true,
      );
    }
  }
}
